import type { Logger as OtelLogger, AnyValueMap } from '@opentelemetry/api-logs'

/**
 * Runtime configuration for the logger
 * Allows dependency injection for testing
 */
export interface RuntimeConfig {
  isBrowser: boolean
  isEdge: boolean
  isProduction: boolean
}

/**
 * Factory function to detect current runtime environment
 * Used as default configuration when not injected
 */
export const getRuntimeConfig = (): RuntimeConfig => ({
  isBrowser: globalThis.window !== undefined,
  isEdge:
    typeof process === 'undefined' ||
    process.env.NEXT_RUNTIME === 'edge' ||
    // @ts-expect-error - EdgeRuntime is only available in edge runtime
    typeof EdgeRuntime !== 'undefined',
  isProduction: process.env.NODE_ENV === 'production',
})

/**
 * # Custom OpenTelemetry Logger
 *
 * ## Why Custom Implementation?
 * Single logging solution for browser + backend instead of learning 2 separate libraries.
 *
 * ### Why not Pino + OpenTelemetry transport?
 * - Pino's transport system uses dynamic imports and worker threads (createRequire)
 * - These don't survive webpack/turbopack bundling in production builds
 * - Setting serverExternalPackages doesn't solve this
 *
 * ### Why not OpenTelemetry SDK everywhere?
 * - Browser: No built-in batching/encryption features
 * - Edge: @opentelemetry/sdk-logs uses Node.js-specific APIs unavailable in edge runtime
 *
 * ## Architecture
 *
 * ### Browser
 * - Web Worker batches logs (10 logs / 2 seconds)
 * - Gzip compression
 * - AES-GCM encryption (secure over network)
 * - Sends to `/api/logger` endpoint
 *
 * ### Edge Runtime (Middleware)
 * - Sends unencrypted JSON to `/api.logger` (internal call, same process)
 * - Requires `APP_URL` env var for absolute URLs, because vercel needs absolute URLs on middleware
 *
 * ### Node.js
 * - Uses OpenTelemetry SDK directly
 * - OTLP exporter to collector
 *
 * ### API Route (`/api.logger`)
 * - Handles both encrypted (browser) and JSON (edge) payloads
 * - Forwards all logs to OpenTelemetry collector
 * - Single path to OTLP = simpler architecture
 *
 * ## OTEL Control
 * - **Production**: Enabled by default (`OBSERVABILITY_ENABLED=true`)
 * - **Development**: Disabled by default, enable with `DEV_OTEL_TESTING=true`
 * - **Disable in prod**: Set `OBSERVABILITY_ENABLED=false`
 *
 * ## Usage Examples
 *
 * ### Error Logging
 * ```typescript
 * logger.error('Validating Dashboard Data', { err: new Error('Stupid Annoying Error') })
 * ```
 *
 * ### Request/Response Logging
 * ```typescript
 * logger.info('Fetching Service Request Data', { req, res })
 * ```
 *
 * ### Child Loggers (Pino-style)
 * ```typescript
 * const moduleLogger = logger.child({ module: 'auth', srNumber: '123' })
 * moduleLogger.info('User logged in')
 * ```
 */
class CustomOtelLogger {
  private otelLogger: OtelLogger | null = null
  private readonly isProduction: boolean
  private readonly isBrowser: boolean
  private readonly isEdge: boolean
  private readonly contextAttributes: AnyValueMap = {}
  private logsWorker: Worker | null = null
  private workerReady = false
  private workerInitPromise: Promise<void> | null = null

  /**
   * Maps log level to console method
   */
  private getConsoleMethod(level: string): 'error' | 'warn' | 'log' {
    if (level === 'error' || level === 'fatal') {
      return 'error'
    }
    if (level === 'warn') {
      return 'warn'
    }
    return 'log'
  }

  /**
   * Parse log inputs and extract message string and attributes
   */
  private parseunknowns(
    message: unknown,
    context: unknown
  ): { logMessage: string; attributes: AnyValueMap } {
    let logMessage: string
    let attributes: AnyValueMap = {}

    // Support pino-style: logger.info({ context }, 'message')
    if (typeof message === 'object' && message !== null) {
      const msgObj = message as Record<string, unknown>

      // Try to extract message from common keys: 'message', 'msg'
      const rawMessage = context ?? msgObj.message ?? msgObj.msg ?? ''
      logMessage =
        typeof rawMessage === 'string' ? rawMessage : JSON.stringify(rawMessage)

      // Remove message/msg from attributes to avoid duplication
      attributes = { ...msgObj } as AnyValueMap
      if (attributes.message) delete attributes.message
      if (attributes.msg) delete attributes.msg
    } else {
      logMessage = String(message)
      attributes = (
        context && typeof context === 'object' && context !== null
          ? context
          : {}
      ) as AnyValueMap
    }

    return { logMessage, attributes }
  }

  /**
   * Log to console (fallback or development mode)
   */
  private logToConsole(
    level: string,
    logMessage: string,
    attributes: AnyValueMap
  ) {
    const consoleMethod = this.getConsoleMethod(level)
    console[consoleMethod](
      logMessage,
      Object.keys(attributes).length > 0 ? attributes : ''
    )
  }

  /**
   * Handle server-side logging (OTel, Edge, or console fallback)
   */
  private handleServerLogging(
    level: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal',
    logMessage: string,
    attributes: AnyValueMap
  ) {
    const otelLogger = this.getOtelLogger()

    if (otelLogger) {
      // Node.js: Use OTLP exporter
      const severityMap = {
        trace: 1, // TRACE
        debug: 5, // DEBUG
        info: 9, // INFO
        warn: 13, // WARN
        error: 17, // ERROR
        fatal: 21, // FATAL
      }

      try {
        otelLogger.emit({
          severityNumber: severityMap[level],
          severityText: level.toUpperCase(),
          body: logMessage,
          attributes,
        })
      } catch (e) {
        // OTel emit failed, fall back to console
        console.error('[OTEL] Emit failed:', e)
        this.logToConsole(level, logMessage, attributes)
      }
    } else if (this.isEdge) {
      // Edge runtime: Send to /api.logger
      this.sendToApiFromEdge(level, logMessage, attributes)
    } else {
      // Node.js with OTEL disabled: Fall back to console
      this.logToConsole(level, logMessage, attributes)
    }
  }

  private getOtelEnabled(): boolean {
    // Only check server env in server-side contexts
    if (this.isBrowser) {
      return false
    }

    try {
      // TO-DO: If this works, can use to reduce duplicate across log-encryption.ts, key-management.ts and crypto
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const { env } = require('@/env/server')
      // Production: enabled by default (OBSERVABILITY_ENABLED=true)
      // Development: only if DEV_OTEL_TESTING=true
      return this.isProduction
        ? env.OBSERVABILITY_ENABLED
        : env.DEV_OTEL_TESTING
    } catch {
      // Env not available, fall back to console
      return false
    }
  }

  private getOtelLogger() {
    // Only use OTEL when enabled AND in Node.js runtime (not Edge or Browser)
    if (!this.getOtelEnabled()) {
      return null // OTEL disabled, console only
    }

    if (!this.isBrowser && !this.isEdge && !this.otelLogger) {
      try {
        // Safe to import in Node.js runtime
        // eslint-disable-next-line @typescript-eslint/no-require-imports
        const { logs } = require('@opentelemetry/api-logs')
        this.otelLogger = logs.getLogger('xpress-logger', '1.0.0')
      } catch {
        // OTEL not available, will fall back to console
      }
    }
    return this.otelLogger
  }

  constructor(
    contextAttributes: AnyValueMap = {},
    runtime: RuntimeConfig = getRuntimeConfig()
  ) {
    this.contextAttributes = contextAttributes
    this.isBrowser = runtime.isBrowser
    this.isEdge = runtime.isEdge
    this.isProduction = runtime.isProduction
  }

  /**
   * Creates a child logger with additional context attributes
   * This is useful for adding module-specific context to logs
   */
  child(bindings: AnyValueMap): CustomOtelLogger {
    // Pass the same runtime config to child logger
    const runtime: RuntimeConfig = {
      isBrowser: this.isBrowser,
      isEdge: this.isEdge,
      isProduction: this.isProduction,
    }
    return new CustomOtelLogger(
      { ...this.contextAttributes, ...bindings },
      runtime
    )
  }

  /**
   * Initialize Web Worker for browser log batching
   * Only called in browser environment
   */
  private async initLogsWorker(): Promise<void> {
    // Return existing promise if already initializing
    if (this.workerInitPromise) {
      return this.workerInitPromise
    }

    // Already initialized
    if (this.workerReady) {
      return
    }

    this.workerInitPromise = new Promise((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(new Error('Worker initialization timeout'))
      }, 2000)

      try {
        this.logsWorker = new Worker('/logs-worker.js')

        this.logsWorker.onmessage = (event) => {
          if (event.data.type === 'READY') {
            clearTimeout(timeout)
            this.workerReady = true
            resolve()
          } else if (event.data.type === 'ERROR') {
            clearTimeout(timeout)
            reject(new Error(event.data.error))
          }
        }

        this.logsWorker.onerror = (error) => {
          clearTimeout(timeout)
          reject(
            new Error(
              `Worker failed to load: ${error.message || 'Unknown error'}`
            )
          )
        }

        // Use dynamic import in async context
        import('@/env/client')
          .then(({ env }) => {
            if (!env.NEXT_PUBLIC_LOG_ENCRYPTION_VALUE) {
              clearTimeout(timeout)
              reject(new Error('NEXT_PUBLIC_LOG_ENCRYPTION_VALUE not set'))
              return
            }

            this.logsWorker!.postMessage({
              type: 'INIT',
              payload: {
                encryptionKey: env.NEXT_PUBLIC_LOG_ENCRYPTION_VALUE,
              },
            })
          })
          .catch((error) => {
            clearTimeout(timeout)
            reject(error instanceof Error ? error : new Error(String(error)))
          })
      } catch (error) {
        clearTimeout(timeout)
        reject(error instanceof Error ? error : new Error(String(error)))
      }
    })

    return this.workerInitPromise
  }

  /**
   * Extract session cookies to forward from Edge runtime to API route
   * By default, cookies are not forwarded in Edge runtime fetch calls
   * @returns Cookie header string or undefined if no cookies found
   */
  private async getSessionCookieHeader(): Promise<string | undefined> {
    try {
      const { cookies } = await import('next/headers')
      const cookieStore = await cookies()
      const cookieParts: string[] = []

      const sessionCookie = cookieStore.get('session')
      if (sessionCookie) {
        cookieParts.push(`${sessionCookie.name}=${sessionCookie.value}`)
      }

      const tempSessionCookie = cookieStore.get('temp_session_id')
      if (tempSessionCookie) {
        cookieParts.push(`${tempSessionCookie.name}=${tempSessionCookie.value}`)
      }

      const trackerId = cookieStore.get('trackerId')
      if (trackerId) {
        cookieParts.push(`${trackerId.name}=${trackerId.value}`)
      }

      return cookieParts.length > 0 ? cookieParts.join('; ') : undefined
    } catch {
      // Cookie forwarding failed, /api/logger will still work but without session
      return undefined
    }
  }

  /**
   * Send logs from Edge runtime to API endpoint
   * Reuses /api/logger route to avoid duplicating OTLP logic
   * Edge runtime requires absolute URL (can't use relative URLs like browser)
   */
  private async sendToApiFromEdge(
    level: string,
    message: string,
    attributes: AnyValueMap
  ) {
    const payload = {
      level,
      message,
      timestamp: new Date().toISOString(),
      ...attributes,
    }

    try {
      // Edge runtime needs absolute URL
      const { env } = await import('@/env/server')

      // Check OTEL enabled status (can't use require() in Edge runtime)
      // Production: enabled by default (OBSERVABILITY_ENABLED=true)
      // Development: only if DEV_OTEL_TESTING=true
      const otelEnabled = this.isProduction
        ? env.OBSERVABILITY_ENABLED
        : env.DEV_OTEL_TESTING

      if (!otelEnabled) {
        // In development without OTEL testing, just use console
        const consoleMethod = this.getConsoleMethod(level)
        console[consoleMethod](message, attributes)
        return
      }

      // Forward session cookies from Edge runtime to API route
      const cookieHeader = await this.getSessionCookieHeader()

      await fetch(`${env.APP_URL}/api/logger`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(cookieHeader && { Cookie: cookieHeader }),
        },
        body: JSON.stringify(payload),
        // Don't use keepalive in Edge runtime (not supported)
      })
    } catch (e) {
      console.error('Edge log API send failed:', e)
      const consoleMethod = this.getConsoleMethod(level)
      console[consoleMethod](message, attributes)
    }
  }

  /**
   * Sends logs from browser to API endpoint
   *
   * Why custom browser implementation:
   * - Web Worker batching reduces network overhead
   * - Client-side encryption protects sensitive log data in transit
   *
   * Fallback order:
   * 1. Web Worker (batched + encrypted) - preferred
   * 2. Direct encryption + send - if worker fails
   * 3. Console logging - if all else fails
   */
  private async sendToApi(
    level: string,
    message: string,
    attributes: AnyValueMap
  ) {
    const payload = {
      level,
      message,
      timestamp: new Date().toISOString(),
      ...attributes,
    }

    // Priority: Web Worker > Direct Encryption + Send > Console
    try {
      await this.initLogsWorker()

      if (this.workerReady && this.logsWorker) {
        this.logsWorker.postMessage({
          type: 'LOG',
          payload,
        })
        return
      }
    } catch {
      // Worker initialization failed, fall through to direct send (silent fallback)
    }

    // Priority 2: Direct Encryption + Send > Console
    try {
      const { encryptLogPayload } = await import('@/lib/crypto/log-encryption')
      const { env } = await import('@/env/client')
      const encryptedPayload = await encryptLogPayload(
        payload,
        env.NEXT_PUBLIC_LOG_ENCRYPTION_VALUE
      )

      await fetch('/api/logger', {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: encryptedPayload,
        keepalive: true,
      })
    } catch (e) {
      console.error('Browser log API send failed:', e)
      const consoleMethod = this.getConsoleMethod(level)
      console[consoleMethod](message, attributes)
    }
  }

  /**
   * Core logging method that routes logs based on runtime environment
   *
   * Routing logic:
   * - Browser → sendToApi (with worker batching + encryption)
   * - Node.js → OTEL SDK (if available) → OTLP exporter
   * - Edge → sendToApiFromEdge (manual OTLP/HTTP)
   * - Fallback → console (always works)
   */
  private log(
    level: 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal',
    message: unknown,
    context?: unknown
  ) {
    // Parse inputs to extract message and attributes
    const { logMessage, attributes } = this.parseunknowns(message, context)

    // Merge context attributes from child loggers
    const allAttributes = { ...this.contextAttributes, ...attributes }

    // Route logs based on environment
    if (this.isBrowser) {
      // Browser: Send to API endpoint (async, fire-and-forget)
      this.sendToApi(level, logMessage, allAttributes)

      // Also log to console in development
      if (!this.isProduction) {
        this.logToConsole(level, logMessage, allAttributes)
      }
    } else {
      // Server: Emit to OpenTelemetry (Node.js only) or fallback to console (Edge)
      this.handleServerLogging(level, logMessage, allAttributes)
    }
  }

  // ==================================== LOGGING METHOGS ==========================
  trace(message: unknown, context?: unknown) {
    this.log('trace', message, context)
  }

  debug(message: unknown, context?: unknown) {
    this.log('debug', message, context)
  }

  info(message: unknown, context?: unknown) {
    this.log('info', message, context)
  }

  warn(message: unknown, context?: unknown) {
    this.log('warn', message, context)
  }

  error(message: unknown, context?: unknown) {
    this.log('error', message, context)
  }

  fatal(message: unknown, context?: unknown) {
    this.log('fatal', message, context)
  }
}

// Export the singleton logger instance with default runtime detection
export const logger = new CustomOtelLogger()

// Export the class for testing with custom runtime configs
export { CustomOtelLogger }

// Export the Logger type for TypeScript users
export type Logger = CustomOtelLogger
